import { type FC, useCallback, useEffect, useState } from "react";

import type {
  DashboardAdminHostBinding,
  DashboardAdminHostFencingDetailResponse,
  DashboardAdminHostFencingEnrollResponse,
  DashboardAdminHostFencingListResponse,
} from "../../dashboard-client";

import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { PanelSection } from "../common/PanelSection";

// ---------------------------------------------------------------------------
// Host enrollment and death attestation.
//
// The irreducible risk this page carries: a host that is merely partitioned
// looks exactly like a host that is dead. Software can refuse an assertion its
// own observations contradict — a recent heartbeat, an unknown last-online
// time, a changed environment set — but it cannot prove an agentless machine
// is off. The wording below says that out loud rather than implying the
// checkboxes make it safe.
// ---------------------------------------------------------------------------

interface AttestationForm {
  assertedState: "destroyed" | "powered_off_retired";
  autoStartDisabled: boolean;
  notRelyingOnReachability: boolean;
  phrase: string;
  reason: string;
  usedProviderControlPlane: boolean;
}

const emptyAttestation: AttestationForm = {
  assertedState: "destroyed",
  autoStartDisabled: false,
  notRelyingOnReachability: false,
  phrase: "",
  reason: "",
  usedProviderControlPlane: false,
};

export const AdminHostFencingTab: FC = () => {
  const [list, setList] =
    useState<DashboardAdminHostFencingListResponse | null>(null);
  const [detail, setDetail] =
    useState<DashboardAdminHostFencingDetailResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [enrollment, setEnrollment] =
    useState<DashboardAdminHostFencingEnrollResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<AttestationForm>(emptyAttestation);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  const loadList = useCallback(async () => {
    setErrorMessage(null);
    const response = await fetch("/admin/host-fencing");
    if (!response.ok) {
      setErrorMessage(await response.text());
      return;
    }
    setList(await response.json());
  }, []);

  const loadDetail = useCallback(async (bindingId: string) => {
    setErrorMessage(null);
    const response = await fetch(
      `/admin/host-fencing/${encodeURIComponent(bindingId)}`,
    );
    if (!response.ok) {
      setErrorMessage(await response.text());
      return;
    }
    setDetail(await response.json());
    setForm(emptyAttestation);
    // A freshly rendered view is a new action, so it gets a new key. Retries
    // of the view already on screen keep the one they have.
    setRequestId(crypto.randomUUID());
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) {
      void loadDetail(selectedId);
    } else {
      setDetail(null);
    }
  }, [loadDetail, selectedId]);

  const attest = async () => {
    if (!detail) {
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    setNotice(null);
    try {
      const response = await fetch(
        `/admin/host-fencing/${encodeURIComponent(detail.binding.id)}/attest`,
        {
          body: JSON.stringify({
            acknowledgements: {
              auto_start_disabled: form.autoStartDisabled,
              not_relying_on_reachability: form.notRelyingOnReachability,
              used_provider_control_plane: form.usedProviderControlPlane,
            },
            asserted_state: form.assertedState,
            confirmation_digest: detail.confirmation_digest,
            confirmation_phrase: form.phrase,
            expected_last_online_at: detail.node_last_online_at,
            reason: form.reason,
            // Minted once per rendered attestation view and REUSED by every
            // retry of it. A key derived from the target digest made
            // re-attestation impossible (the second attestation of the same
            // host has the same targets); a key minted per click lost lost-
            // response idempotency, because a retry after a dropped response
            // would arrive as a second distinct action. Per-view is the seam
            // that gets both: a genuinely new attestation reloads the detail
            // and gets a new key.
            request_id: requestId,
          }),
          headers: { "content-type": "application/json" },
          method: "POST",
        },
      );
      if (!response.ok) {
        setErrorMessage(await response.text());
        return;
      }
      setNotice(
        "Attestation recorded. This binding is permanently retired. The evidence authorizes transfers for 30 minutes; if no destination capacity appears in that window, attest again against a fresh target set.",
      );
      await loadList();
      await loadDetail(detail.binding.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelSection title="Host Fencing">
      <div className="space-y-4">
        <Alert variant="warning">
          Retiring a host binding is irreversible. Ping, timeout, HTTP failure
          and ICMP are not proof of death: a partitioned host is still running
          and its containers are still writing. Attesting a host that is
          actually alive can corrupt workspaces with two writers.
        </Alert>
        {list && !list.fencing_enabled && (
          <Alert variant="info">
            ARTI_CHANNEL_ENV_VPS_OWNER_FENCING is off. Attestation is refused
            while it is off; retired bindings stay retired regardless.
          </Alert>
        )}
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
        {notice && <Alert variant="success">{notice}</Alert>}
        {enrollment && <EnrollmentResult enrollment={enrollment} />}
        <EnrollForm
          isProcessing={busy}
          onEnrolled={async (result) => {
            setEnrollment(result);
            await loadList();
          }}
          onError={setErrorMessage}
          setProcessing={setBusy}
        />
        <BindingTable
          bindings={list?.bindings ?? []}
          onSelect={setSelectedId}
          selectedId={selectedId}
        />
        {detail && (
          <AttestationPanel
            detail={detail}
            form={form}
            isProcessing={busy}
            onChange={setForm}
            onSubmit={() => void attest()}
          />
        )}
      </div>
    </PanelSection>
  );
};

const EnrollmentResult: FC<{
  enrollment: DashboardAdminHostFencingEnrollResponse;
}> = ({ enrollment }) => (
  <Alert variant="success">
    <div className="space-y-2">
      <div>
        Enrolled {enrollment.binding.host_identity} as node{" "}
        <code>{enrollment.binding.node_id}</code>. Configure the node with
        exactly these values — a node that publishes anything else is not
        admitted.
      </div>
      <pre className="overflow-x-auto bg-slate-950/60 p-2 font-mono text-[11px]">
        {Object.entries(enrollment.node_configuration)
          .map(([key, value]) => `${key}=${value}`)
          .join("\n")}
      </pre>
    </div>
  </Alert>
);

const EnrollForm: FC<{
  isProcessing: boolean;
  onEnrolled: (
    result: DashboardAdminHostFencingEnrollResponse,
  ) => Promise<void>;
  onError: (message: string) => void;
  setProcessing: (value: boolean) => void;
}> = ({ isProcessing, onEnrolled, onError, setProcessing }) => {
  const [accountId, setAccountId] = useState("");
  const [region, setRegion] = useState("");
  const [hostIdentity, setHostIdentity] = useState("");
  const [reason, setReason] = useState("");

  const submit = async () => {
    setProcessing(true);
    try {
      const response = await fetch("/admin/host-fencing/enroll", {
        body: JSON.stringify({
          host_identity: hostIdentity,
          provider_account_id: accountId,
          provider_region: region,
          reason,
        }),
        headers: { "content-type": "application/json" },
        method: "POST",
      });
      if (!response.ok) {
        onError(await response.text());
        return;
      }
      await onEnrolled(await response.json());
      setAccountId("");
      setRegion("");
      setHostIdentity("");
      setReason("");
    } finally {
      setProcessing(false);
    }
  };

  const complete =
    accountId.trim() && region.trim() && hostIdentity.trim() && reason.trim();

  return (
    <section className="space-y-2 border border-slate-700/70 bg-slate-950/40 p-3">
      <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        Enroll external VPS
      </h3>
      <p className="font-mono text-[11px] text-slate-400">
        The node id and host incarnation are generated here and returned once.
        They are never accepted from the node.
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        <LabelledInput
          label="Provider account / deployment scope"
          onChange={setAccountId}
          value={accountId}
        />
        <LabelledInput
          label="Provider site / region"
          onChange={setRegion}
          value={region}
        />
        <LabelledInput
          label="Immutable VM identity"
          onChange={setHostIdentity}
          value={hostIdentity}
        />
        <LabelledInput
          label="Reason / ticket"
          onChange={setReason}
          value={reason}
        />
      </div>
      <Button
        disabled={isProcessing || !complete}
        onClick={() => void submit()}
        size="sm"
        type="button"
      >
        Enroll
      </Button>
    </section>
  );
};

const LabelledInput: FC<{
  label: string;
  onChange: (value: string) => void;
  value: string;
}> = ({ label, onChange, value }) => (
  <label className="block space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
    <span>{label}</span>
    <input
      className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
      onChange={(event) => onChange(event.target.value)}
      value={value}
    />
  </label>
);

const BindingTable: FC<{
  bindings: readonly DashboardAdminHostBinding[];
  onSelect: (bindingId: string) => void;
  selectedId: string | null;
}> = ({ bindings, onSelect, selectedId }) => (
  <section className="overflow-x-auto border border-slate-700/70 bg-slate-950/40">
    <table className="min-w-full divide-y divide-slate-800 text-left font-mono text-[11px]">
      <thead className="bg-slate-950/60 uppercase tracking-[0.18em] text-slate-500">
        <tr>
          <th className="px-3 py-2">Node</th>
          <th className="px-3 py-2">Host</th>
          <th className="px-3 py-2">Incarnation</th>
          <th className="px-3 py-2">Scope</th>
          <th className="px-3 py-2">State</th>
          <th className="px-3 py-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800 text-slate-300">
        {bindings.length === 0 && (
          <tr>
            <td className="px-3 py-2 text-slate-500" colSpan={6}>
              No host bindings are enrolled.
            </td>
          </tr>
        )}
        {bindings.map((binding) => (
          <tr key={binding.id}>
            <td className="px-3 py-2 text-slate-100">{binding.node_id}</td>
            <td className="px-3 py-2">{binding.host_identity}</td>
            <td className="px-3 py-2" title={binding.host_incarnation_id}>
              {binding.host_incarnation_id.slice(0, 8)}…
            </td>
            <td className="px-3 py-2">
              {binding.provider_account_id} / {binding.provider_region}
            </td>
            <td className="px-3 py-2">
              {binding.retired_at ? (
                <span className="text-rose-400">
                  retired {new Date(binding.retired_at).toLocaleString()}
                </span>
              ) : (
                <span className="text-emerald-400">active</span>
              )}
            </td>
            <td className="px-3 py-2">
              <Button
                onClick={() => onSelect(binding.id)}
                size="sm"
                type="button"
                variant={selectedId === binding.id ? "primary" : "ghost"}
              >
                Inspect
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

const AttestationPanel: FC<{
  detail: DashboardAdminHostFencingDetailResponse;
  form: AttestationForm;
  isProcessing: boolean;
  onChange: (form: AttestationForm) => void;
  onSubmit: () => void;
}> = ({ detail, form, isProcessing, onChange, onSubmit }) => {
  const retired = detail.binding.retired_at !== null;
  const phraseMatches = form.phrase === detail.confirmation_phrase;
  const acknowledged =
    form.autoStartDisabled &&
    form.notRelyingOnReachability &&
    form.usedProviderControlPlane;
  // A retired binding may be attested AGAIN. Retirement is permanent and
  // nothing here reverses it; what expires is the 30-minute window in which
  // evidence can authorize a transfer. If that window closed before capacity
  // appeared, the environments are still stranded on a host that is still
  // dead, and re-attestation is the only way to move them.
  const ready =
    detail.fencing_enabled &&
    detail.offline_precondition_met &&
    detail.targets.length > 0 &&
    phraseMatches &&
    acknowledged &&
    form.reason.trim().length > 0;

  return (
    <section className="space-y-3 border border-rose-700/50 bg-slate-950/40 p-3">
      <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        Attest host death — {detail.binding.host_identity}
      </h3>
      <dl className="grid gap-1 font-mono text-[11px] text-slate-400 md:grid-cols-2">
        <Fact label="Node" value={detail.binding.node_id} />
        <Fact label="Incarnation" value={detail.binding.host_incarnation_id} />
        <Fact label="Node status" value={detail.node_status ?? "unknown"} />
        <Fact
          label="Last online"
          value={detail.node_last_online_at ?? "never recorded"}
        />
        <Fact
          label="Offline for"
          value={
            detail.node_offline_ms === null
              ? "unknown"
              : `${Math.floor(detail.node_offline_ms / 60_000)} min`
          }
        />
        <Fact
          label="Target digest"
          value={detail.confirmation_digest.slice(0, 16)}
        />
      </dl>
      {retired && (
        <Alert variant="warning">
          This binding is already retired and can never be reactivated. You may
          still attest again to authorize a fresh 30-minute transfer window for
          the environments it still owns; enroll a successor only once none
          remain.
        </Alert>
      )}
      {!detail.offline_precondition_met && (
        <Alert variant="error">
          This node has either answered within the last five minutes or has no
          recorded last-online time. Both refuse attestation: elapsed silence is
          not evidence, but a recent heartbeat is a contradiction.
        </Alert>
      )}
      <TargetTable targets={detail.targets} />
      <p className="font-mono text-[11px] text-amber-300">
        &ldquo;Powered off and retired&rdquo; means the VM must never be
        resumed, restored from saved state, or powered on unchanged. A saved or
        suspended VM is NOT powered off — it can resume execution directly.
      </p>
      <Acknowledgements form={form} onChange={onChange} />
      <LabelledInput
        label="Reason / incident reference"
        onChange={(reason) => onChange({ ...form, reason })}
        value={form.reason}
      />
      <label className="block space-y-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        <span>Type to confirm: {detail.confirmation_phrase}</span>
        <input
          className="w-full border border-slate-700 bg-slate-900 px-3 py-2 text-sm normal-case tracking-normal text-slate-100 outline-none focus:border-cyan-400/70"
          onChange={(event) =>
            onChange({ ...form, phrase: event.target.value })
          }
          value={form.phrase}
        />
      </label>
      <div className="flex items-center gap-3">
        <select
          className="border border-slate-700 bg-slate-900 px-2 py-1 font-mono text-[11px] text-slate-100"
          onChange={(event) =>
            onChange({
              ...form,
              assertedState:
                event.target.value === "powered_off_retired"
                  ? "powered_off_retired"
                  : "destroyed",
            })
          }
          value={form.assertedState}
        >
          <option value="destroyed">destroyed</option>
          <option value="powered_off_retired">powered_off_retired</option>
        </select>
        <Button
          disabled={!ready || isProcessing}
          onClick={onSubmit}
          size="sm"
          type="button"
          variant="danger"
        >
          Retire binding and record evidence
        </Button>
      </div>
    </section>
  );
};

const Acknowledgements: FC<{
  form: AttestationForm;
  onChange: (form: AttestationForm) => void;
}> = ({ form, onChange }) => (
  <div className="space-y-1 font-mono text-[11px] text-slate-300">
    <Checkbox
      checked={form.usedProviderControlPlane}
      label="I confirmed the power state in the hosting provider's control plane."
      onChange={(usedProviderControlPlane) =>
        onChange({ ...form, usedProviderControlPlane })
      }
    />
    <Checkbox
      checked={form.notRelyingOnReachability}
      label="I did not rely on ping, HTTP, or any reachability check as evidence."
      onChange={(notRelyingOnReachability) =>
        onChange({ ...form, notRelyingOnReachability })
      }
    />
    <Checkbox
      checked={form.autoStartDisabled}
      label="Any auto-start, restore, or revival mechanism for this VM is disabled."
      onChange={(autoStartDisabled) => onChange({ ...form, autoStartDisabled })}
    />
  </div>
);

const Checkbox: FC<{
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}> = ({ checked, label, onChange }) => (
  <label className="flex items-start gap-2">
    <input
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      type="checkbox"
    />
    <span>{label}</span>
  </label>
);

const TargetTable: FC<{
  targets: DashboardAdminHostFencingDetailResponse["targets"];
}> = ({ targets }) => (
  <div className="overflow-x-auto border border-slate-800">
    <table className="min-w-full divide-y divide-slate-800 text-left font-mono text-[11px]">
      <thead className="bg-slate-950/60 uppercase tracking-[0.18em] text-slate-500">
        <tr>
          <th className="px-3 py-2">Environment</th>
          <th className="px-3 py-2">Organization</th>
          <th className="px-3 py-2">Status</th>
          <th className="px-3 py-2">Owner tuple</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800 text-slate-300">
        {targets.length === 0 && (
          <tr>
            <td className="px-3 py-2 text-slate-500" colSpan={4}>
              This host owns no environments; there is nothing to fence.
            </td>
          </tr>
        )}
        {targets.map((target) => (
          <tr key={target.environment_id}>
            <td className="px-3 py-2">{target.environment_id}</td>
            <td className="px-3 py-2">{target.organization_id}</td>
            <td className="px-3 py-2">{target.status}</td>
            <td className="px-3 py-2">
              {target.owner_node_id}/{target.owner_boot_id ?? "—"}/
              {target.owner_epoch}/{target.owner_lease_epoch}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Fact: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="text-slate-500">{label}:</span> {value}
  </div>
);
